export type TipoDocumento   = 'receta' | 'orden' | 'laboratorio' | 'presupuesto' | 'plan-tratamiento' | 'consentimiento';
export type DisenoPlantilla = 'clasico' | 'minimalista' | 'corporativo' | 'barra' | 'personalizada';
export type TamanoHoja      = 'a4' | 'carta' | 'oficio' | 'a5' | 'media-carta';
export type Orientacion     = 'vertical' | 'horizontal';

/** Dimensiones en px @ 96 dpi, mapeadas al tamaño de hoja real (siempre en vertical) */
export const TAMANO_INFO: Record<TamanoHoja, { label: string; width: number; height: number; cssSize: string }> = {
  'a4':          { label: 'A4  (210 × 297 mm)',   width: 794,  height: 1123, cssSize: 'A4'           },
  'carta':       { label: 'Carta  (8.5 × 11 in)', width: 816,  height: 1056, cssSize: 'letter'       },
  'oficio':      { label: 'Oficio  (8.5 × 14 in)',width: 816,  height: 1344, cssSize: 'legal'        },
  'a5':          { label: 'A5  (148 × 210 mm)',   width: 559,  height: 794,  cssSize: 'A5'           },
  'media-carta': { label: 'Media carta  (5.5 × 8.5 in)', width: 528, height: 816, cssSize: '5.5in 8.5in' },
};

export const ORIENTACION_INFO: Record<Orientacion, { label: string }> = {
  vertical:   { label: 'Vertical' },
  horizontal: { label: 'Horizontal' },
};

/** Dimensiones + cssSize ajustados a la orientación elegida */
export function getDimensiones(tamano: TamanoHoja, orientacion: Orientacion): { width: number; height: number; cssSize: string } {
  const info = TAMANO_INFO[tamano];
  if (orientacion === 'horizontal') {
    return { width: info.height, height: info.width, cssSize: `${info.cssSize} landscape` };
  }
  return { width: info.width, height: info.height, cssSize: info.cssSize };
}

export interface ClinicaInfo {
  nombre: string;
  cedula?: string;
  direccion?: string;
  telefono?: string;
  logoUrl?: string;
}

export interface MedicoInfo {
  nombre: string;
  cedula?: string;
  especialidad?: string;
}

export interface PacienteInfo {
  nombre: string;
  sexo?: string;
  fechaNacimiento?: string;
}

export interface MedicacionItem {
  /** Denominación genérica (principio activo) — obligatoria por COFEPRIS / LGS */
  denominacionGenerica: string;
  /** Denominación distintiva (nombre comercial/marca) — opcional */
  denominacionDistintiva?: string;
  dosis?: string;
  frecuencia?: string;
  duracion?: string;
  viaAdministracion?: string;
  indicaciones?: string;
}

export interface EstudioItem {
  nombre: string;
  descripcion?: string;
}

export interface PresupuestoItem {
  servicio: string;
  descripcion?: string;
  cantidad: number;
  precioUnitario: number;
}

export interface PlanTratamientoItem {
  tratamiento: string;
  descripcion?: string;
  sesiones?: number;
  duracion?: string;
}

export interface TemplateData {
  tipo: TipoDocumento;
  folio: string;
  fecha: string;
  generadoPor?: string;
  impresoPor?: string;
  clinica: ClinicaInfo;
  medico: MedicoInfo;
  paciente: PacienteInfo;
  fechaConsulta?: string;
  medicaciones?: MedicacionItem[];
  instrucciones?: string;
  estudios?: EstudioItem[];
  qrUrl?: string;
  // presupuesto
  presupuestoItems?: PresupuestoItem[];
  presupuestoDescuento?: number;
  presupuestoIva?: number;
  presupuestoValidezDias?: number;
  presupuestoMoneda?: string;
  // plan de tratamiento
  planTratamientoItems?: PlanTratamientoItem[];
  diagnostico?: string;
  objetivoTratamiento?: string;
  duracionTratamiento?: string;
  frecuenciaTratamiento?: string;
  // consentimiento
  consentimientoTitulo?: string;
  consentimientoContenido?: string;
  firmaUrl?: string;
  firmaEsPdf?: boolean;
  firmadoEn?: string;
}

export const TIPO_LABEL: Record<TipoDocumento, string> = {
  receta:             'Receta de Medicamentos',
  orden:              'Orden Médica',
  laboratorio:        'Solicitud de Laboratorio',
  presupuesto:        'Presupuesto',
  'plan-tratamiento': 'Plan de Tratamiento',
  consentimiento:     'Consentimiento Informado',
};

export const SECCION_LABEL: Record<TipoDocumento, string> = {
  receta:             'Medicamentos',
  orden:              'Indicaciones',
  laboratorio:        'Estudios solicitados',
  presupuesto:        'Servicios y costos',
  'plan-tratamiento': 'Tratamientos',
  consentimiento:     'Consentimiento',
};

export const DISENO_INFO: Record<DisenoPlantilla, { label: string; descripcion: string }> = {
  clasico:       { label: 'Clásico Formal',   descripcion: 'Doble línea azul, nombre en mayúsculas, caduceo de fondo. Estilo médico tradicional.' },
  minimalista:   { label: 'Minimalista',       descripcion: 'Sin decoración, separadores finos, tipografía clara. Moderno y muy legible.' },
  corporativo:   { label: 'Corporativo',       descripcion: 'Banda de color en cabecera y pie. Fuerte identidad de marca.' },
  barra:         { label: 'Barra Lateral',     descripcion: 'Sidebar izquierdo con logo y datos de clínica. Layout diferenciado.' },
  personalizada: { label: 'Personalizada',     descripcion: 'Diseña tu propio formato desde cero con el editor visual.' },
};

/** Tags simples (un solo valor) disponibles al diseñar una plantilla Personalizada, por tipo de documento */
export interface MergeTagInfo { tag: string; desc: string }
export interface MergeTagLoopInfo { campo: string; label: string; snippet: string }

const TAGS_BASE: MergeTagInfo[] = [
  { tag: '{{clinica.nombre}}',    desc: 'Nombre de la clínica' },
  { tag: '{{clinica.direccion}}', desc: 'Dirección de la clínica' },
  { tag: '{{clinica.telefono}}',  desc: 'Teléfono de la clínica' },
  { tag: '{{clinica.cedula}}',    desc: 'Cédula/registro de la clínica' },
  { tag: '{{clinica.logoUrl}}',   desc: 'URL del logo (úsalo como src de una imagen)' },
  { tag: '{{medico.nombre}}',     desc: 'Nombre del médico' },
  { tag: '{{medico.cedula}}',     desc: 'Cédula profesional del médico' },
  { tag: '{{medico.especialidad}}', desc: 'Especialidad del médico' },
  { tag: '{{paciente.nombre}}',   desc: 'Nombre del paciente' },
  { tag: '{{paciente.sexo}}',     desc: 'Sexo del paciente' },
  { tag: '{{paciente.fechaNacimiento}}', desc: 'Fecha de nacimiento del paciente' },
  { tag: '{{folio}}',             desc: 'Folio del documento' },
  { tag: '{{fecha}}',             desc: 'Fecha de emisión' },
];

function loopSnippet(campo: string, filas: string): MergeTagLoopInfo['snippet'] {
  return `<table style="width:100%;border-collapse:collapse">\n  <tbody>\n    <tr data-loop="${campo}">\n${filas}\n    </tr>\n  </tbody>\n</table>`;
}

/** Snippet apilado: nombre del medicamento arriba, dosis/frecuencia/duración abajo — mismo estilo que Clásico/Minimalista/Corporativo/Barra */
function medicacionStackedSnippet(): MergeTagLoopInfo['snippet'] {
  return (
    '<div data-loop="medicaciones" style="padding:8px 0;border-bottom:1px solid #f0f0f0">\n' +
    '  <div style="font-weight:700;font-size:12px">{{item.denominacionGenerica}}</div>\n' +
    '  <div style="font-size:11px;color:#444;margin-top:2px">\n' +
    '    Dosis: <b>{{item.dosis}}</b> &nbsp;&nbsp; Frecuencia: <b>{{item.frecuencia}}</b> &nbsp;&nbsp; Duración: <b>{{item.duracion}}</b>\n' +
    '  </div>\n' +
    '</div>'
  );
}

export const MERGE_TAGS: Record<TipoDocumento, { simples: MergeTagInfo[]; loop?: MergeTagLoopInfo }> = {
  receta: {
    simples: [...TAGS_BASE, { tag: '{{instrucciones}}', desc: 'Instrucciones generales' }],
    loop: {
      campo: 'medicaciones',
      label: 'Bloque repetible: Medicamentos',
      snippet: medicacionStackedSnippet(),
    },
  },
  orden: {
    simples: [...TAGS_BASE, { tag: '{{instrucciones}}', desc: 'Indicaciones' }],
  },
  laboratorio: {
    simples: TAGS_BASE,
    loop: {
      campo: 'estudios',
      label: 'Fila repetible: Estudios',
      snippet: loopSnippet('estudios', '      <td>{{item.nombre}}</td>\n      <td>{{item.descripcion}}</td>'),
    },
  },
  presupuesto: {
    simples: [...TAGS_BASE,
      { tag: '{{presupuestoDescuento}}', desc: 'Descuento aplicado (%)' },
      { tag: '{{presupuestoValidezDias}}', desc: 'Días de validez' },
      { tag: '{{presupuestoMoneda}}', desc: 'Moneda' },
    ],
    loop: {
      campo: 'presupuestoItems',
      label: 'Fila repetible: Servicios',
      snippet: loopSnippet('presupuestoItems',
        '      <td>{{item.servicio}}</td>\n      <td>{{item.descripcion}}</td>\n      <td>{{item.cantidad}}</td>\n      <td>{{item.precioUnitario}}</td>'),
    },
  },
  'plan-tratamiento': {
    simples: [...TAGS_BASE,
      { tag: '{{diagnostico}}', desc: 'Diagnóstico' },
      { tag: '{{objetivoTratamiento}}', desc: 'Objetivo del tratamiento' },
      { tag: '{{duracionTratamiento}}', desc: 'Duración total del tratamiento' },
      { tag: '{{frecuenciaTratamiento}}', desc: 'Frecuencia de sesiones' },
    ],
    loop: {
      campo: 'planTratamientoItems',
      label: 'Fila repetible: Tratamientos',
      snippet: loopSnippet('planTratamientoItems',
        '      <td>{{item.tratamiento}}</td>\n      <td>{{item.descripcion}}</td>\n      <td>{{item.sesiones}}</td>\n      <td>{{item.duracion}}</td>'),
    },
  },
  consentimiento: {
    simples: [...TAGS_BASE,
      { tag: '{{consentimientoTitulo}}', desc: 'Título del consentimiento' },
      { tag: '{{consentimientoContenido}}', desc: 'Texto completo del consentimiento' },
    ],
  },
};

const hoy = new Date().toLocaleDateString('es-MX');

export const SAMPLE_DATA: TemplateData = {
  tipo: 'receta',
  folio: '0054932',
  fecha: hoy,
  generadoPor: 'sistema',
  impresoPor: 'Dr. Martínez',
  clinica: {
    nombre: 'Consultorio Nimbo',
    cedula: '1234567',
    direccion: 'Edison 2525, Garza Nieto, Monterrey NL C.P. 49000',
    telefono: '81 2397-1768',
  },
  medico: {
    nombre: 'Dr. Alberto Martínez López',
    cedula: '12345678',
    especialidad: 'Medicina General',
  },
  paciente: {
    nombre: 'John Doe',
    sexo: 'Masculino',
    fechaNacimiento: '01/01/1990',
  },
  fechaConsulta: hoy,
  medicaciones: [
    { denominacionGenerica: 'Paracetamol 500mg',       dosis: '1 tableta',  frecuencia: 'Cada 8 horas',          duracion: '5 días', indicaciones: 'Tomar después de alimentos' },
    { denominacionGenerica: 'Amoxicilina 875mg/125mg', dosis: '1 tableta',  frecuencia: 'Cada 12 horas',         duracion: '7 días' },
    { denominacionGenerica: 'Ibuprofeno 400mg',         dosis: '1 tableta',  frecuencia: 'Cada 8 horas si hay dolor', duracion: '3 días', indicaciones: 'No tomar con el estómago vacío' },
  ],
  instrucciones: 'Reposo relativo por 48 horas. Hidratación abundante. Regresar si los síntomas persisten o se agravan.',
  estudios: [
    { nombre: 'Biometría Hemática Completa', descripcion: 'Con diferencial' },
    { nombre: 'Química Sanguínea 27 elementos' },
    { nombre: 'Examen General de Orina' },
  ],
  presupuestoItems: [
    { servicio: 'Consulta médica',        descripcion: 'Primera evaluación integral', cantidad: 1, precioUnitario: 800 },
    { servicio: 'Análisis de sangre',     descripcion: 'BHC + QS 27 elementos',       cantidad: 1, precioUnitario: 650 },
    { servicio: 'Ultrasonido abdominal',  descripcion: 'Sin contraste',                cantidad: 1, precioUnitario: 1200 },
  ],
  presupuestoDescuento: 10,
  presupuestoIva: 0,
  presupuestoValidezDias: 30,
  presupuestoMoneda: 'MXN',
  planTratamientoItems: [
    { tratamiento: 'Evaluación inicial',   descripcion: 'Historial clínico completo y diagnóstico',              sesiones: 1,  duracion: '60 min' },
    { tratamiento: 'Fisioterapia',         descripcion: 'Sesiones de rehabilitación muscular y movilidad',       sesiones: 10, duracion: '45 min c/u' },
    { tratamiento: 'Seguimiento y alta',   descripcion: 'Revisión de avances y ajuste final del plan',           sesiones: 3,  duracion: '30 min c/u' },
  ],
  diagnostico:           'Lumbalgia mecánica crónica',
  objetivoTratamiento:   'Reducción del dolor y recuperación de movilidad lumbar',
  duracionTratamiento:   '2 meses',
  frecuenciaTratamiento: '3 sesiones por semana',
  consentimientoTitulo:  'Consentimiento informado para procedimiento médico',
  consentimientoContenido:
    'Declaro que he sido informado(a) de manera clara y suficiente sobre la naturaleza, propósito, riesgos, ' +
    'beneficios y alternativas del procedimiento propuesto. He tenido la oportunidad de resolver mis dudas con ' +
    'el personal médico y otorgo mi consentimiento de manera libre y voluntaria para su realización.\n\n' +
    'Entiendo que puedo revocar este consentimiento en cualquier momento antes del procedimiento.',
};
